// Web Push registration for the browser side. Pairs with public/sw.js
// (the push handler) and src/api/pushApi.ts (the backend calls).
//
// Flow: registerServiceWorker() once on app start; the NotificationsScreen
// toggle calls enablePush() (asks permission -> pushManager.subscribe ->
// POST /push/subscribe) and disablePush() (unsubscribe + DELETE). The
// VAPID public key is embedded at build time as VITE_VAPID_PUBLIC_KEY.

import { subscribeWeb, unsubscribeWeb } from '../api/pushApi';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY ?? '';
const API_BASE = import.meta.env.VITE_AUTH_API_BASE ?? '/api-auth';
const SW_URL = `/sw.js?api=${encodeURIComponent(API_BASE)}`;

export function isPushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function pushPermission(): NotificationPermission {
  if (typeof Notification === 'undefined') return 'denied';
  return Notification.permission;
}

/** Fire-and-forget: register the SW so it's ready before the user opts in. */
export async function registerServiceWorker(): Promise<void> {
  if (!isPushSupported() || !VAPID_PUBLIC_KEY) return;
  try {
    await navigator.serviceWorker.register(SW_URL, { scope: '/' });
  } catch {
    /* a failed registration just means push stays unavailable */
  }
  // The SW posts {type:'zmist:navigate', url} when a notification is tapped
  // and an app tab is already open - send that tab to the channel/message.
  try {
    navigator.serviceWorker.addEventListener('message', (event) => {
      const data = event.data as { type?: string; url?: string } | undefined;
      if (data?.type === 'zmist:navigate' && data.url) {
        const current = window.location.pathname + window.location.search;
        if (current !== data.url) window.location.assign(data.url);
      }
    });
  } catch {
    /* navigation-from-notification is a nice-to-have */
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const buffer = new ArrayBuffer(raw.length);
  const out = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function readySW(): Promise<ServiceWorkerRegistration> {
  await navigator.serviceWorker.register(SW_URL, { scope: '/' });
  return navigator.serviceWorker.ready;
}

/** True once a subscription exists AND the backend has it. */
export async function isPushEnabled(): Promise<boolean> {
  if (!isPushSupported()) return false;
  try {
    const reg = await navigator.serviceWorker.getRegistration('/');
    const sub = await reg?.pushManager.getSubscription();
    return !!sub;
  } catch {
    return false;
  }
}

export type EnablePushResult =
  | { ok: true }
  | { ok: false; reason: 'unsupported' | 'denied' | 'no-key' | 'error'; error?: string };

export async function enablePush(): Promise<EnablePushResult> {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' };
  if (!VAPID_PUBLIC_KEY) return { ok: false, reason: 'no-key' };

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { ok: false, reason: 'denied' };

  try {
    const reg = await readySW();
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    }
    const json = sub.toJSON();
    await subscribeWeb({
      platform: 'web',
      endpoint: sub.endpoint,
      p256dh: json.keys?.p256dh ?? '',
      auth: json.keys?.auth ?? '',
    });
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: 'error', error: e instanceof Error ? e.message : String(e) };
  }
}

export async function disablePush(): Promise<void> {
  if (!isPushSupported()) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration('/');
    const sub = await reg?.pushManager.getSubscription();
    if (sub) {
      const endpoint = sub.endpoint;
      await sub.unsubscribe().catch(() => undefined);
      await unsubscribeWeb(endpoint).catch(() => undefined);
    }
  } catch {
    /* ignore */
  }
}
